import { useState, useCallback } from 'react'
import useDealStore from '../store/dealStore'
import useAuthStore from '../store/authStore'
import { SEVERITY_CONFIG, CATEGORY_LABELS, MODULE_NAMES } from '../data/workflowEngine'
import { sendAlertDigestEmail } from '../services/workflowNotificationService'
import '../styles/actionCenter.css'

const TABS = [
  { id: 'all', label: 'All', severities: ['overdue', 'urgent', 'warning', 'info'] },
  { id: 'urgent', label: 'Action Required', severities: ['overdue', 'urgent'] },
  { id: 'warning', label: 'Warnings', severities: ['warning'] },
  { id: 'info', label: 'Info', severities: ['info'] },
]

const SNOOZE_OPTIONS = [
  { label: '1 hour', minutes: 60 },
  { label: '4 hours', minutes: 240 },
  { label: 'Tomorrow', minutes: 1440 },
]

// ── Single alert card ──────────────────────────────────────────────────────────
function AlertCard({ alert, onNavigate, onDismiss, onSnooze, onRead }) {
  const [snoozeOpen, setSnoozeOpen] = useState(false)
  const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info

  const handleNavigate = () => {
    onRead(alert.id)
    onNavigate(alert.moduleNumber)
  }

  return (
    <div
      className={`ac-alert-card severity-${alert.severity} ${alert.read ? 'read' : 'unread'}`}
      style={{ borderLeftColor: cfg.color }}
    >
      {/* Unread dot */}
      {!alert.read && <span className="ac-unread-dot" style={{ background: cfg.color }} />}

      <div className="ac-alert-top">
        <div className="ac-alert-meta">
          <span className="ac-severity-badge" style={{ background: cfg.bg, color: cfg.color, borderColor: cfg.border }}>
            {cfg.icon} {cfg.label}
          </span>
          {alert.moduleNumber > 0 && (
            <span className="ac-module-tag">
              Module {alert.moduleNumber} — {MODULE_NAMES[alert.moduleNumber] || ''}
            </span>
          )}
          {alert.category && (
            <span className="ac-category-tag">{CATEGORY_LABELS[alert.category] || alert.category}</span>
          )}
        </div>

        <div className="ac-alert-actions">
          {alert.dismissible && (
            <button
              className="ac-icon-btn"
              title="Dismiss"
              onClick={() => onDismiss(alert.id)}
            >✕</button>
          )}
          {!alert.persistent && (
            <div className="ac-snooze-wrap">
              <button
                className="ac-icon-btn"
                title="Snooze"
                onClick={() => setSnoozeOpen(o => !o)}
              >⏱</button>
              {snoozeOpen && (
                <div className="ac-snooze-menu">
                  {SNOOZE_OPTIONS.map(opt => (
                    <button
                      key={opt.minutes}
                      onClick={() => { onSnooze(alert.id, opt.minutes); setSnoozeOpen(false) }}
                    >{opt.label}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {alert.persistent && (
            <span className="ac-persistent-badge" title="Cannot be dismissed until resolved">📌</span>
          )}
        </div>
      </div>

      <h4 className="ac-alert-title">{alert.title}</h4>
      <p className="ac-alert-message">{alert.message}</p>

      <div className="ac-alert-footer">
        <button className="ac-action-btn" style={{ background: cfg.color }} onClick={handleNavigate}>
          {alert.actionText} →
        </button>
        <span className="ac-alert-time">
          {new Date(alert.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>
    </div>
  )
}

// ── Summary bar at top ─────────────────────────────────────────────────────────
function AlertSummaryBar({ alerts }) {
  const counts = { overdue: 0, urgent: 0, warning: 0, info: 0 }
  alerts.forEach(a => { if (counts[a.severity] !== undefined) counts[a.severity]++ })

  return (
    <div className="ac-summary-bar">
      {Object.entries(counts).map(([sev, count]) => {
        if (!count) return null
        const cfg = SEVERITY_CONFIG[sev]
        return (
          <div key={sev} className="ac-summary-chip" style={{ background: cfg.bg, borderColor: cfg.border }}>
            <span style={{ color: cfg.color }}>{cfg.icon} {count}</span>
            <span style={{ color: cfg.color, opacity: 0.75 }}>{cfg.label}</span>
          </div>
        )
      })}
      {Object.values(counts).every(c => c === 0) && (
        <div className="ac-summary-chip all-clear">
          <span> All clear — no pending alerts</span>
        </div>
      )}
    </div>
  )
}

// ── Empty state ────────────────────────────────────────────────────────────────
function EmptyState({ tab }) {
  const msgs = {
    urgent: { icon: '', title: 'No urgent actions', sub: 'All decision points and critical items are clear.' },
    warning: { icon: '👍', title: 'No warnings', sub: 'No module gaps or document issues detected.' },
    info: { icon: '📋', title: 'No info alerts', sub: 'Everything is running smoothly.' },
    all: { icon: '🎉', title: 'All clear!', sub: 'No pending alerts. Keep the pipeline moving.' },
  }
  const m = msgs[tab] || msgs.all
  return (
    <div className="ac-empty">
      <div className="ac-empty-icon">{m.icon}</div>
      <strong>{m.title}</strong>
      <p>{m.sub}</p>
    </div>
  )
}

// ── Main ActionCenter component ────────────────────────────────────────────────
export default function ActionCenter() {
  const {
    getNotifications, getUnreadCount,
    markNotificationRead, markAllNotificationsRead,
    dismissNotification, snoozeNotification,
    navigateToModule, dealState,
  } = useDealStore()

  const { user } = useAuthStore()

  const [activeTab, setActiveTab] = useState('all')
  const [emailSending, setEmailSending] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const allAlerts = getNotifications()
  const unreadCount = getUnreadCount()
  const dealName = dealState.dealName || 'Current Deal'
  const userEmail = user?.email || ''

  // Filter alerts by tab and search
  const tabDef = TABS.find(t => t.id === activeTab) || TABS[0]
  const filtered = allAlerts
    .filter(a => tabDef.severities.includes(a.severity))
    .filter(a => !searchQuery || (
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.message.toLowerCase().includes(searchQuery.toLowerCase())
    ))

  // Auto-send on mount is removed — emails now only go out via the 4-hour
  // idle notification system (idleNotificationService.js). The "Email Digest"
  // button below still lets users trigger a manual send at any time.

  const handleSendDigest = useCallback(async () => {
    setEmailSending(true)
    await sendAlertDigestEmail(allAlerts, dealName, userEmail)
    setEmailSending(false)
    setEmailSent(true)
    setTimeout(() => setEmailSent(false), 3000)
  }, [allAlerts, dealName, userEmail])

  const handleNavigate = useCallback((moduleNumber) => {
    if (moduleNumber > 0) navigateToModule(moduleNumber)
  }, [navigateToModule])

  const urgentCount = allAlerts.filter(a => ['urgent', 'overdue'].includes(a.severity)).length
  const warningCount = allAlerts.filter(a => a.severity === 'warning').length

  return (
    <div className="action-center">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="ac-header">
        <div className="ac-header-left">
          <h2 className="ac-title">
            <span className="ac-title-icon">🎯</span>
            Action Center
            {unreadCount > 0 && (
              <span className="ac-unread-badge">{unreadCount} new</span>
            )}
          </h2>
          <p className="ac-subtitle">
            {allAlerts.length === 0
              ? 'No pending actions — your pipeline is on track.'
              : `${urgentCount > 0 ? `${urgentCount} urgent, ` : ''}${warningCount} warning${warningCount !== 1 ? 's' : ''} across your deal pipeline.`
            }
          </p>
        </div>
        <div className="ac-header-right">
          {unreadCount > 0 && (
            <button className="ac-btn-secondary" onClick={markAllNotificationsRead}>
              Mark all read
            </button>
          )}
          <button
            className="ac-btn-secondary"
            onClick={handleSendDigest}
            disabled={emailSending || allAlerts.length === 0}
          >
            {emailSending ? '⏳ Sending…' : emailSent ? '✓ Sent!' : '📧 Email Digest'}
          </button>
        </div>
      </div>

      {/* ── Summary bar ────────────────────────────────────────────────────── */}
      <AlertSummaryBar alerts={allAlerts} />

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      {allAlerts.length > 3 && (
        <div className="ac-search-wrap">
          <input
            className="ac-search"
            placeholder="Search alerts…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="ac-search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="ac-tabs">
        {TABS.map(tab => {
          const count = allAlerts.filter(a => tab.severities.includes(a.severity)).length
          return (
            <button
              key={tab.id}
              className={`ac-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {count > 0 && <span className="ac-tab-count">{count}</span>}
            </button>
          )
        })}
      </div>

      {/* ── Alert list ─────────────────────────────────────────────────────── */}
      <div className="ac-alert-list">
        {filtered.length === 0 ? (
          <EmptyState tab={activeTab} />
        ) : (
          filtered.map(alert => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onNavigate={handleNavigate}
              onDismiss={dismissNotification}
              onSnooze={snoozeNotification}
              onRead={markNotificationRead}
            />
          ))
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div className="ac-footer">
        <span>Alerts update automatically as you work through each module.</span>
        <span>Persistent alerts cannot be dismissed until the underlying issue is resolved.</span>
      </div>
    </div>
  )
}
